import { Modal, Typography } from 'antd';
import { type FC, useEffect, useRef, useState } from 'react';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { t } from '../i18n';
import { GeolocationStatus, useStore } from '../store';

const { Text } = Typography;

export const OwnLocationManager: FC = () => {
  const watchId = useRef<number | null>(null);
  const geolocationStatus = useStore((state) => state.timer.geolocation.status);
  const setGeolocationStatus = useStore((state) => state.actions.setGeolocationStatus);
  const setOwnLocation = useStore((state) => state.actions.setOwnLocation);
  const [showModal, setShowModal] = useState(false);

  function bootstrapGeolocationWatcher() {
    watchId.current = startGeolocationWatcher({
      setGeolocationStatus,
      setOwnLocation,
      onCancelFollowing: handleCancelFollowing,
    });
  }

  async function initGeolocation() {
    if (geolocationStatus !== GeolocationStatus.SEARCHING) return;

    const permission = await checkGeolocationPermission();
    if (permission === 'granted') {
      bootstrapGeolocationWatcher();
      return;
    }

    // if state changed during the permission check, do not show the modal
    if (geolocationStatus !== GeolocationStatus.SEARCHING) return;
    setShowModal(true);
  }

  function handleCancelFollowing() {
    if (watchId.current == null) return;

    window.navigator.geolocation.clearWatch(watchId.current);
    watchId.current = null;
    setGeolocationStatus(GeolocationStatus.OFF);
    setOwnLocation(null);
  }

  async function handleOk() {
    const permission = await checkGeolocationPermission();
    setShowModal(false);

    if (permission === 'denied') {
      setGeolocationStatus(GeolocationStatus.OFF);
      return;
    }
    bootstrapGeolocationWatcher();
  }

  function handleCancelModal() {
    setShowModal(false);
    setGeolocationStatus(GeolocationStatus.OFF);
  }

  useEffect(() => {
    if (geolocationStatus === GeolocationStatus.OFF && watchId.current != null) {
      handleCancelFollowing();
    }

    initGeolocation();
  }, [geolocationStatus]);

  return (
    <Modal
      title={t('ownlocation.permission.title')}
      open={showModal}
      onCancel={() => handleCancelModal()}
      centered
      onOk={() => handleOk()}
    >
      <Text>{t('ownlocation.permission.message')}</Text>
    </Modal>
  );
};

async function checkGeolocationPermission(): Promise<PermissionState> {
  if (navigator.permissions == null) {
    // Permissions API not supported, will trigger browser permission prompt when requesting geolocation
    return 'prompt';
  }

  try {
    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
    return permissionStatus.state;
  } catch (error) {
    console.error('Error checking geolocation permission:', error);
    return 'prompt'; // Assume denied if there's an error checking permissions
  }
}

const startGeolocationWatcher = ({
  setGeolocationStatus,
  setOwnLocation,
  onCancelFollowing,
}: {
  setGeolocationStatus: (status: GeolocationStatus) => void;
  setOwnLocation: (location: { lat: number; lng: number; opacity: number } | null) => void;
  onCancelFollowing: () => void;
}): number => {
  return window.navigator.geolocation.watchPosition(
    (position) => {
      if (position != null && position.coords != null) {
        const latlng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          opacity: 1,
        };
        setGeolocationStatus(GeolocationStatus.FOLLOWING);
        setOwnLocation(latlng);
        sendAnalyticsEvent('geolocation-success');
      } else {
        sendAnalyticsEvent('geolocation-fail');
      }
    },
    () => {
      onCancelFollowing();
      alert(t("We couldn't determine your position"));
      sendAnalyticsEvent('geolocation-fail');
    },
    {
      enableHighAccuracy: true,
      timeout: 5000,
    },
  );
};
